import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '@/lib/api';
import { useAuth } from './AuthContext';
import type { MyPermissions } from '@easyflow/shared';

/**
 * Permessi effettivi dell'utente = ruolo ∪ concessioni vive (COSA).
 * Il DOVE (recinto effettivo) è risolto dal backend una volta per richiesta e
 * NON è esposto per permesso (PM-019): il FE non ricalcola nulla, legge solo
 * `/roles/my-permissions` via apiClient.
 */
interface PermissionContextType {
  permissions: string[];
  loading: boolean;
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (codes: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType>({
  permissions: [],
  loading: true,
  hasPermission: () => true,
  hasAnyPermission: () => true,
  refreshPermissions: async () => {},
});

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    if (isAdmin) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.get<MyPermissions>('/roles/my-permissions');
      const payload = res.data;
      if (payload && Array.isArray(payload.permissions)) {
        setPermissions(payload.permissions);
      } else {
        setPermissions([]);
        }
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = useCallback((code: string) => {
    if (isAdmin) return true;
    if (loading) return false;
    return permissions.includes(code);
  }, [permissions, loading, isAdmin]);

  const hasAnyPermission = useCallback((codes: string[]) => {
    if (isAdmin) return true;
    if (loading) return false;
    return codes.some(code => permissions.includes(code));
  }, [permissions, loading, isAdmin]);

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        loading,
        hasPermission,
        hasAnyPermission,
        refreshPermissions: fetchPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export const usePermissions = () => useContext(PermissionContext);
